import { PairedSalesStudy } from '@aloft/toolkit-shared';
import { StyleSheet, View } from '@react-pdf/renderer';

import { Icon } from '../../../components/pdf-export/Icon/Icon.js';
import { Page } from '../../../components/pdf-export/Page/Page.js';
import { Text } from '../../../components/pdf-export/Text/Text.js';
import { Document } from '../../../summary/Document.js';
import { createStudyResultString } from '../utils/createStudyResultString.js';
import { getIcon } from '../utils/getIcon.js';
import { ReconciliationStats } from './ReconciliationStats.js';
import { StudyTable } from './StudyTable.js';

const styles = StyleSheet.create({
  titleWrapper: {
    marginTop: 8,
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleText: {
    // this is to get the text to align nicely with the icon. there may be a
    // better way; i thought alignItems center would be enough but it isn't.
    top: 5,
  },
  commentaryWrapper: {
    marginBottom: 8,
  },
  statsWrapper: {
    marginLeft: 12,
  },
});

function StudySummary({ study }: { study: PairedSalesStudy }) {
  const title = createStudyResultString(
    study.input.analysisFeature,
    study.body.reconciledValue,
    'per',
  );
  const icon = getIcon(study.input.analysisFeature);
  const hasNotes = !!study.body.notes.trim().length;
  const hasMultiplePairs = study.body.selectedPairs.length > 1;

  return (
    <View>
      <View style={styles.titleWrapper}>
        <Icon icon={icon} />
        <View style={styles.titleText}>
          <Text variant="h3">{title}</Text>
        </View>
      </View>
      {hasNotes && (
        <View style={styles.commentaryWrapper}>
          <Text variant="body">{study.body.notes}</Text>
        </View>
      )}
      <View>
        <StudyTable study={study} />
      </View>
      {hasMultiplePairs && (
        <View style={styles.statsWrapper}>
          <ReconciliationStats
            pairs={study.body.selectedPairs}
            reconciledValue={study.body.reconciledValue}
            feature={study.input.analysisFeature}
          />
        </View>
      )}
    </View>
  );
}

const studiesExportStyles = StyleSheet.create({
  wrapper: {
    flexDirection: 'column',
    gap: 24,
  },
});

type Props = {
  studies: PairedSalesStudy[];
};

export function StudiesExport({ studies }: Props) {
  return (
    <Document>
      <Page title="Paired Sales Analysis">
        <View style={studiesExportStyles.wrapper}>
          {studies.map((study) => (
            <StudySummary key={study.id} study={study} />
          ))}
        </View>
      </Page>
    </Document>
  );
}
